import React, { useState, useEffect, useCallback, useRef, useContext } from 'react'
import Widget from '../../../dpe-usco/components/Widget/Widget'
import TabbedTable from '../../../components/TabbedTable/TabbedTable'
import TableStatus from '../../../components/subComponents/TableStatus/TableStatus'
import { getSalesYear } from '../../../services/apiHelper'
import { GlobalContext } from '../../../services/context'
import {
  convertToSentenceCase,
  yearsOptionFactory
} from '../../../services/utils'
import moment from 'moment'
import Select from '../../../dpe-usco/components/Select/Select'

const generateDeliveryMonths = year => {
  const startDate = year ? `${year}-09-01` : new Date().getFullYear() + '-09-01'
  const momentDate = moment(startDate)
  const _monthArray = []
  const _monthObj = {}
  for (let i = 0;i < 13;i++) {
    const newDate = momentDate.clone().add(i, 'month')
    const delivery_month = newDate.format('MMM-YY')
    const month = newDate.format('MMMM').toLowerCase()
    const year = newDate.format('YYYY')
    _monthArray.push({
      delivery_month,
      available_acres: 0,
      available_units: 0
    })
    _monthObj[delivery_month] = {
      month,
      year,
      available_acres: 0,
      available_units: 0
    }
  }
  return { _monthObj, _monthArray }
}

const { _monthObj, _monthArray } = generateDeliveryMonths()

const COMMERCIAL_FEED = 'Commercial feed'

const ViewProdPlanDeliveryWidget = ({
  aph,
  contractType,
  initialUnits,
  contractYear,
  prodPlanYear,
  monthlyUnits,
  unitType,
  premium_type,
  status,
  filterHandler,
  totalReceived,
  grainReceivers,
  prodPlanData,
  companyId
}) => {
  const [silageInputs, setSilageInputs] = useState({ ..._monthObj })
  const [earlageInputs, setEarlageInputs] = useState({ ..._monthObj })
  const [highMoistureCornInputs, setHighMoistureCornInputs] = useState({
    ..._monthObj
  })
  const [grainInputs, setGrainInputs] = useState({ ..._monthObj })
  const [, setMonthArray] = useState([..._monthArray])
  const [usedInitialYear, setUsedInitialYear] = useState(false)
  const [unitOfMeasure, setUnitOfMeasure] = useState()
  const [currentProductionPlan, setCurrentProductionPlan] = useState([])
  const [tabs, setTabs] = useState([
    {
      value: 'Grain',
      showTooltip:true,
      structure: {
        headers: [
          'Delivery month ',
          'Total acres',
          'Total bu.',
          'Contracted acres',
          'Contracted bu.',
          'Available acres',
          'Available bu.',
          'Harvest bu.',
          'Received bu.',
          'Premium'
        ],
        dataKeys: [
          'delivery_month',
          'total_acres',
          'total_units',
          'contract_acres',
          'contract_units',
          'available_acres',
          'available_units',
          'harvest_bushels',
          'net_quantity',
          'premium'
        ]
      }
    }
  ])
 
  const getProductTypeState = useCallback(
    type => {
      const silage = {
        type: 'silage',
        state: silageInputs,
        setter: setSilageInputs
      }

      const earlage = {
        type: 'earlage',
        state: earlageInputs,
        setter: setEarlageInputs
      }

      const highMoistureCorn = {
        type: 'high-moisture',
        state: highMoistureCornInputs,
        setter: setHighMoistureCornInputs
      }
      const grain = {
        type: 'grain',
        state: grainInputs,
        setter: setGrainInputs
      }
      if (contractType === COMMERCIAL_FEED) {
        switch (type) {
          case 'SILAGE':
            return silage
          case 'EARLAGE':
            return earlage
          case 'HIGH MOISTURE CORN':
            return highMoistureCorn
          case 'GRAIN':
            return grain
          case 'ALL':
            return [silage, earlage, grain, highMoistureCorn]
          default:
            return null
        }
      } else {
        switch (type) {
          case 'GRAIN':
            return grain
          case 'ALL':
            return [grain]
          default:
            return null
        }
      }
    },
    [
      contractType,
      silageInputs,
      setSilageInputs,
      earlageInputs,
      setEarlageInputs,
      highMoistureCornInputs,
      setHighMoistureCornInputs,
      grainInputs,
      setGrainInputs
    ]
  )

  const [defaultContractYear, setDefaultContractYear] = useState(
    new Date().getFullYear().toString()
  )
  useEffect(() => {
    const init = async () => {
      await getSalesYear().then(sales_year => {
        setDefaultContractYear(sales_year.data.sales_year)
      })
    }
    if (prodPlanYear) {
      init()
    }
  }, [prodPlanYear])

  useEffect(() => {
    if (unitType && !unitOfMeasure) {
      setUnitOfMeasure(unitType === 'bushels' ? 'bu' : 'acres')
    }
  }, [initialUnits, unitOfMeasure, unitType])

  const prevContractYear = useRef()
  useEffect(() => {
    const handleSelectYear = option => {
      const allInputs = getProductTypeState('ALL')
      // This shouldn't delete all the user data
      allInputs.forEach(({ setter }) => {
        const { _monthObj } = generateDeliveryMonths(option.id)
        setter(_monthObj)
      })
      const { _monthArray } = generateDeliveryMonths(option.id)
      setMonthArray([..._monthArray])
    }
    if (contractYear && !usedInitialYear) {
      prevContractYear.current = contractYear
      setUsedInitialYear(true)
      handleSelectYear({ id: contractYear })
    } else if (prevContractYear.current !== contractYear) {
      prevContractYear.current = contractYear
      handleSelectYear({ id: contractYear })
    }
  }, [contractYear, getProductTypeState, usedInitialYear])

  const initialPass = useRef(true)
  useEffect(() => {
    if (
      monthlyUnits &&
      monthlyUnits.length &&
      contractType &&
      initialPass.current
    ) {
      const allInputs = getProductTypeState('ALL')
      allInputs.forEach(({ type, setter }) => {
        const initialType = monthlyUnits.filter(unit => {
          return unit.end_product_type === type
        })
        if (initialType.length) {
          setter(prev => {
            const temp = { ...prev }
            for (let monthInput in temp) {
              initialType.forEach(unit => {
                if (
                  temp[monthInput].month === unit.month &&
                  temp[monthInput].year === unit.year
                ) {
                  temp[monthInput].available_acres = unit.available_acres
                  temp[monthInput].available_units = unit.available_units
                }
              })
            }
            return temp
          })
        }
      })
      initialPass.current = false
    }
  }, [contractType, getProductTypeState, monthlyUnits, prodPlanYear])
  

  /*eslint-disable*/
  useEffect(() => {
    const generateTabs = () => {
      const tabGenerator = (type, monthlyUnits) => {
        const measureUnit = type === 'silage'? 'tons' : 'bu' 
        const value = convertToSentenceCase(type)
        let data = monthlyUnits ? monthlyUnits[type] : []
        let year
        let responseObj = {}
        if (monthlyUnits && monthlyUnits.contract_year) {
          year = monthlyUnits.contract_year
          delete monthlyUnits.contract_year
        }
        if(contractYear){
          year = contractYear
        }
        for (let type in monthlyUnits) {
          let sumTotalAcres = 0
          let sumTotalUnits = 0
          let sumContractAcres = 0
          let sumContractUnits = 0
          let sumAvailableAcres = 0
          let sumAvailableUnits = 0
          let sumReceivedUnits = 0 
          let sumHarvestBushels = 0

          let checkDeliveryTypeData = false
          monthlyUnits[type].forEach(element =>{
            if(element.total_units || element.total_acres) checkDeliveryTypeData = true
          })
          
          if (prodPlanYear && totalReceived && checkDeliveryTypeData) {
            sumReceivedUnits = totalReceived 
          }

          if (sumReceivedUnits) {
            let totalReceivedForYear = totalReceived
            const maxLength = monthlyUnits[type].length
            let lastIndex = 0
            monthlyUnits[type].forEach((monthObj, index) => {
              if (monthObj.harvest_bushels) { // update months with total units
                let monthHarvestBu = parseFloat(monthObj.harvest_bushels || 0)
                if (totalReceivedForYear >= monthHarvestBu) {
                  monthObj.net_quantity = monthHarvestBu
                  totalReceivedForYear -= monthHarvestBu
                } else { //Total Recevied is less than contract ammount
                  monthObj.net_quantity = totalReceivedForYear
                  totalReceivedForYear = 0
                }
                lastIndex = index
              } else if ((index === maxLength -1) && totalReceivedForYear) { //If we reach end of array, give rest of total to the last month with total_units
                monthlyUnits[type][lastIndex].net_quantity = totalReceivedForYear + monthlyUnits[type][lastIndex].net_quantity
              }
            })
          } 
          monthlyUnits[type].forEach(unit => {
            if (unit.available_acres){ 
              sumAvailableAcres += Math.round(parseFloat(unit.available_acres) || 0)
            }
            if (unit.available_units){ 
              sumAvailableUnits += Math.round(parseFloat(unit.available_units) || 0)
            }
            if (unit.total_acres) {
              sumTotalAcres += Math.round(parseFloat(unit.total_acres) || 0)
            }
            if (unit.total_units) {
              sumTotalUnits += Math.round(parseFloat(unit.total_units) || 0)
            }
            if (unit.contract_acres) {
              sumContractAcres += Math.round(parseFloat(unit.contract_acres) || 0)
            }
            if (unit.contract_units) {
              sumContractUnits += Math.round(parseFloat(unit.contract_units) || 0)
            }
            if (unit.harvest_bushels) {
              sumHarvestBushels += Math.round(parseFloat(unit.harvest_bushels) || 0)
            }
          })
          responseObj[type] = {
            sumTotalAcres,
            sumTotalUnits,
            sumContractAcres,
            sumContractUnits,
            sumAvailableAcres,
            sumAvailableUnits,
            sumHarvestBushels,
            sumReceivedUnits
          }
        }
        const arrayOfSums = [
          'sumTotalAcres',
          'sumTotalUnits',
          Number(aph) && 'sumContractAcres',
          'sumContractUnits',
          'sumAvailableAcres',
          'sumAvailableUnits',
          'sumHarvestBushels',
          'sumReceivedUnits'
        ].filter(Boolean)
        const sumTotal = (type, dataKey, status) => {
          let sum = 0
          if (status !== 'EMPTY' && responseObj[type] && responseObj[type][dataKey]) {
            sum = Math.round(responseObj[type][dataKey] || 0)
          }
          return <div className="flx1 tal miw70 df jcfs pl5 mr1%">{sum.toLocaleString()}</div> 
        }
        
        const adjustedData = data.map(month => {
          if(month.harvest_bushels < month.net_quantity) {
            const newMonth = { ...month }
            newMonth.net_quantity = month.harvest_bushels
            return newMonth
          }
          else {
            return month
          }
        })

        data = adjustedData
       

        return {
          value,
          data,
          structure: {
            dataKeys : [
              'delivery_month',
              'total_acres',
              'total_units',
              Number(aph) && 'contract_acres',
              'contract_units',
              'available_acres',
              'available_units',
              'harvest_bushels',
              'net_quantity',
              contractType === 'Fuel' && 'premium'
            ].filter(Boolean),
            headers: [
              'Delivery month',
              'Total acres',
              `Total ${measureUnit}.`,
              Number(aph) && 'Contracted acres',
              `Contracted  ${measureUnit}.`,
              'Available acres',
              `Available  ${measureUnit}.`,
              `Harvest  ${measureUnit}.`,
              `Received  ${measureUnit}.`,
              contractType === 'Fuel' && 'Premium'
            ].filter(Boolean), 
            showTooltip:true,
            rowFormat: {
              delivery_month: {
                overwriteStyles: true,
                customStyle: 'df aic flx1'
              },
              total_acres: {
                format: total_acres => {
                  return <span className="pl10">{total_acres ? `${Math.round(total_acres).toLocaleString()}` : '-'}</span>
                }
              },
              total_units: {
                format: total_units => {
                  return <span className="pl10">{total_units ? (grainReceivers ? `${Math.round(total_units).toLocaleString()}` : `${Math.round(total_units).toLocaleString()}  ${measureUnit}`) : '-'}</span>
                }
              },
              available_acres: {
                format: available_acres => {
                  return <span className="pl10">{available_acres ? `${Math.round(available_acres).toLocaleString()}` : '-'}</span>
                }
              },
              available_units: {
                format: available_units => {
                  return <span className="pl10">{available_units ? ( grainReceivers ? `${Math.round(available_units).toLocaleString()}` : `${Math.round(available_units).toLocaleString()}  ${measureUnit}`) : '-'}</span>
                }
              },
              contract_acres: {
                format: contract_acres => {
                  return <span className="pl10">{contract_acres ? `${Math.round(contract_acres).toLocaleString()}` : '-'}</span>
                }
              },
              contract_units: {
                format: contract_acres => {
                  return <span className="pl10">{contract_acres ? `${Math.round(contract_acres).toLocaleString()}` : '-'}</span>
                }
              },
              net_quantity: {
                format: net_quantity => {
                  return <span className="pl10">{net_quantity ? ( grainReceivers ? `${Math.round(net_quantity).toLocaleString()}` : `${Math.round(net_quantity)}  ${measureUnit}`) : '-'}</span>
                }
              },
              premium: {
                format: premium => {
                  return <span className="pl10">${premium ? premium : parseFloat(0).toFixed(2)+'/'+((premium_type==='bushels')? measureUnit:'ac')}</span>
                }
              },
              harvest_bushels: {
                format: harvest_bushels => {
                  return <span className="pl10">{harvest_bushels ? ( grainReceivers ? `${Math.round(harvest_bushels).toLocaleString()}` : `${Math.round(harvest_bushels).toLocaleString()}  ${measureUnit}`) : '-'}</span>
                }
              }
            }
          },
          bottomOfTable: <div className="bt1-s-lightgray df jcfe aic pl15 pr15 h44">
            <div className="flx1 tal miw70 df jcfs ">Totals</div>
            {arrayOfSums.map(dataKey => {
              return sumTotal(type, dataKey, status)})
            }
            {contractType === 'Fuel' ? <div className="flx1 tal miw70 df jcfs "></div> : null}
          </div>
        }
      }
      const grain = tabGenerator('grain', monthlyUnits)
      if (contractType === COMMERCIAL_FEED) {
        return [
          grain,
          tabGenerator('silage', monthlyUnits),
          tabGenerator('earlage', monthlyUnits),
          tabGenerator('high-moisture', monthlyUnits)
        ]
      } else {
        return [grain]
      }
    }
    if(monthlyUnits) {
      const tabs = generateTabs()
      setTabs(tabs)
    }
  }, [status, prodPlanYear, contractType, contractYear, monthlyUnits, totalReceived, aph, grainReceivers, premium_type])

  const MAX_YEARS = 1
  const MIN_YEARS = -4
  const yearsOptions = yearsOptionFactory(parseInt(defaultContractYear), MIN_YEARS, MAX_YEARS)

  const { dispatch, trackEvent } = useContext(GlobalContext)
  const clickYear = (e, obj) => {
    e.preventDefault()
    const detailView = {
      title: `Production Plan: ${obj.contract_year}`,
      previous: obj.company_name
    }
    trackEvent('production_plan_link_clicked', { companyId: companyId, productionPlanDetails: obj })
    dispatch({ type: 'GO_TO_VIEW_PRODUCTION_PLAN', payload: { companyId, productionPlanDetails: obj, detailView } })
  }

  useEffect(() => {
    let tempPlanData = []
    if(prodPlanYear) {
      tempPlanData = prodPlanData.filter(element => element.contract_year === parseInt(prodPlanYear))
    }
    setCurrentProductionPlan(tempPlanData)
  },[prodPlanData,prodPlanYear])

  const renderSelectRow = (
    <div className="df w300 jcsb aic">
      <div className="c-teal-500">
        {currentProductionPlan.length>0 ?
          <a
            className="c-teal-500 c-teal-500:h"
            href={`production-plan/${currentProductionPlan[0].production_plan_id}`}
            onClick={e => {
              clickYear(e, currentProductionPlan[0])
            }}
          >
          View production plan
          </a> : 'View production plan'
        }
      </div>
      <div className="w100">
        <Select
          handleSelection={year => {
            filterHandler(year.value)
          }}
          defaultValue={prodPlanYear}
          options={yearsOptions}
        />
      </div>
    </div>
  )
  return (
    <Widget title="Delivery" button={prodPlanYear ? renderSelectRow : null}>
      <TabbedTable
        IsInputTable={false}
        editMode={false}
        tabs={tabs}
        statusComponent={
          status ? (
            <TableStatus
              status={status}
              prompt={
                status 
                  ? 'No production plans found for specified sales year'
                  : 'No data'
              }
            />
          ) : null
        }
        grainReceivers={grainReceivers}
      />
    </Widget>
  )
}

export default ViewProdPlanDeliveryWidget
